import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PoliciesContextService } from '@core/services/context/policies-context.service';
import { AuthService } from '@core/services/core/auth/auth.service';
import { ContextService } from '@core/services/core/context.service';
import { LogService } from '@core/services/core/log.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { DispatchPostMessageOptions, EventsHelperService } from '@core/services/helpers/events-helper.service';
import { HealthAppService } from '@core/services/http-services/health-app.service';
import { HealthAppDispatchEventData, HealthAppLoginResponse, HealthAppOperationalAccess, HealthAppReceivedEventData } from '@models/health-app';
import { isServeMode } from '@utils/utils';
import { race, Subscription, throwError, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-health-embedded-access',
  templateUrl: './health-embedded-access.component.html',
  styleUrls: ['./health-embedded-access.component.scss'],
})
export class HealthEmbeddedAccessComponent implements OnInit, OnDestroy {

  @ViewChild('iframe') iframe: ElementRef;
  dataLoaded = false;
  responseOk = false;
  iframeUrlSafeResource: SafeResourceUrl;
  healthAppOrigin = '';
  healthAppContext = '';
  healthAppCredentials: HealthAppLoginResponse;
  healthAppEventsSubscription: Subscription;
  iframeLoaded = false;
  hideIframe = true;

  @Input() operational: HealthAppOperationalAccess;
  @Input() minHeight = false;
  @Input() pageCanReturn = false;
  @Input() hideKoBtns = false;
  @Output() close = new EventEmitter();

  constructor(
    public ctx: ContextService,
    private log: LogService,
    private eventsHelper: EventsHelperService,
    private domSanitizer: DomSanitizer,
    private healthAppService: HealthAppService,
    private auth: AuthService,
    private nav: NavigationHelper,
    private ui: UiHelperService,
    private policiesContext: PoliciesContextService
    ) { }

  ngOnInit() {
    if (!this.operational || !this.isOfTypeEmbeddedOperationalAccess(this.operational)) {
      this.showError();
    }
    this.healthAppOrigin = this.eventsHelper.getHealthAppOrigin();
    this.healthAppContext = this.eventsHelper.getHealthAppContext();
    if (!this.healthAppOrigin) {
      this.showError();
      return;
    }

    this.loadHealthApp();
  }

  ngOnDestroy() {
    if (this.healthAppEventsSubscription) {
      this.healthAppEventsSubscription.unsubscribe();
    }
  }

  showError() {
    this.dataLoaded = true;
    this.responseOk = false;
  }

  private setDataLoading() {
    this.dataLoaded = false;
  }

  private setDataLoaded() {
    this.dataLoaded = true;
  }

  private setResponseOk() {
    this.responseOk = true;
  }

  private setResponseKo() {
    this.responseOk = false;
  }

  setHealthAppUrl() {
    let iframeUnsafeUrl: string;
    iframeUnsafeUrl = `${this.healthAppOrigin}/health-app/${this.operational}`;
    if (this.healthAppContext && !isServeMode() ) {
      iframeUnsafeUrl = `${this.healthAppOrigin}/${this.healthAppContext}/health-app/${this.operational}`;
    }
    this.iframeUrlSafeResource = this.domSanitizer.bypassSecurityTrustResourceUrl(iframeUnsafeUrl);
  }

  goBack() {
    if (this.pageCanReturn) {
      this.nav.pop();
      return;
    }
    this.goHomePage();
  }

  goHomePage() {
    this.nav.goHomePage();
  }

  private async loadHealthApp() {
    const { user } = this.auth.getState();

    if (!user) { this.showError(); }

    this.setDataLoading();
    await this.ui.presentLoading(`health-app/${this.operational}`);
    this.healthAppService.getHealthAppLoginCredentials(user?.documentNumber)
      .subscribe((res: HealthAppLoginResponse) => {
        this.healthAppCredentials = res;
        this.setDataLoaded();
        this.setResponseOk();
        this.setHealthAppUrl();
        this.startPostMessageCommunication();
      }, _ => {
        this.setResponseKo();
      });
  }

  private startPostMessageCommunication() {
    const allowedOrigins = [this.healthAppOrigin];
    const getPostMessages$ = this.eventsHelper.getAllowedOriginPostMessages(allowedOrigins)
    .pipe(filter(message => this.messageHasDataType(message)),
      map(message => message?.data as HealthAppReceivedEventData));

    const timeoutSecs = 15000;
    const timeout$ = timer(timeoutSecs).pipe(switchMap(x => throwError(`Iframe didn't dispatch any message within ${timeoutSecs / 1000} seconds`)));

    this.healthAppEventsSubscription = race(getPostMessages$, timeout$).subscribe((message: HealthAppReceivedEventData) => {
      this.handleIncomingEmbeddedEvent(message);
    }, async (err) => {
      await this.ui.dismissLoading(`health-app/${this.operational}`);
      this.log.e(this, err);
      this.showError();
    });

  }

  private async handleIncomingEmbeddedEvent(message: HealthAppReceivedEventData) {
    const iframeNativeElement: HTMLIFrameElement = this.iframe?.nativeElement;
    const iframeWindow: Window = iframeNativeElement?.contentWindow;
    switch (message?.type) {
      case 'iframeLoaded':
        this.iframeLoaded = true;
        this.ui.dismissLoading(`health-app/${this.operational}`);
        this.hideIframe = false;
        break;
      case 'closeIframe':
        this.close.emit('closeIframe');
        break;
      case 'authRequired':
        const configuration: DispatchPostMessageOptions<HealthAppDispatchEventData> = {
          target: iframeWindow,
          targetOrigin: this.healthAppOrigin,
          message: { type: 'login', payload: this.healthAppCredentials}
        };
        this.eventsHelper.dispatchPostMessageEvent(configuration);
        break;
      case 'error':
        this.responseOk = false;
        break;
      case 'authorizationRequestCorrect':
        this.policiesContext.emitPolicyAuthorizatiosnReloadNeeded(true);
        this.policiesContext.emitChangeToAuthorizationsSection(true);
        break;
      case 'appointmentChangeSection':
        this.policiesContext.emitChangeToAppointmentsSection(true);
        break;
      default:
        this.log.d(this, 'Unidentified Salud event: ', message);
        break;
    }

  }

  private isOfTypeEmbeddedOperationalAccess(operational): operational is HealthAppOperationalAccess {
    return Object.values(HealthAppOperationalAccess).includes(operational);
  }

  private messageHasDataType(message: MessageEvent) {
    return !!message?.data?.type;
  }

}
